import { keyframes } from '@emotion/react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { CamptainMark } from '@/components/brand/CamptainMark';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RING_COUNT = 4;
const RING_INSET = 11;

export interface CamptainLoaderProps {
  /** Outer diameter in px. */
  size?: number;
  label?: string;
  /** Text color for `label`; use a light color on a dark backdrop. */
  labelColor?: string;
}

/**
 * Branded ring spinner — concentric rotating rings (theme primary/secondary)
 * around the Camptain logo. Drop-in replacement for a bare CircularProgress
 * wherever the app shows a spinner.
 */
export function CamptainLoader({ size = 96, label, labelColor = 'text.secondary' }: CamptainLoaderProps) {
  const theme = useTheme();
  const ringColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.primary.light,
    theme.palette.secondary.light,
  ];

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}
    >
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        {Array.from({ length: RING_COUNT }, (_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              inset: i * RING_INSET,
              borderRadius: '50%',
              border: '3px solid transparent',
              borderTopColor: ringColors[i % ringColors.length],
              animation: `${spin} ${2.4 + i * 0.5}s cubic-bezier(0.17, 0.49, 0.96, 0.79) infinite`,
              animationDirection: i % 2 === 1 ? 'reverse' : 'normal',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        ))}
        <CamptainMark
          size="38%"
          sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        />
      </Box>
      {label ? (
        <Typography variant="body2" sx={{ color: labelColor }}>
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

export interface CamptainLoaderOverlayProps {
  open: boolean;
  label?: string;
}

/** Full-screen overlay for a background action in flight (bulk update, export, import…). */
export function CamptainLoaderOverlay({ open, label = 'Processing…' }: CamptainLoaderOverlayProps) {
  const theme = useTheme();
  return (
    <Backdrop
      open={open}
      sx={{
        color: '#fff',
        zIndex: theme.zIndex.modal + 1,
        bgcolor: theme.tokens.surface.scrim,
      }}
    >
      <CamptainLoader size={110} label={label} labelColor="common.white" />
    </Backdrop>
  );
}
